import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Global.css";
import styles from "./Login.module.css";
import WelcomePanel from "../components/auth/WelcomePanel";
import SignUpForm from "../components/auth/SignUpForm";
import { useAuthContext } from "../contexts/AuthContext";
import Footer from "../components/Footer";
import {
  buildSignupJourneyMetadata,
  peekAuthReturn,
  rememberAuthReturn,
  safeInternalPath,
} from "../utils/conversionJourney";
import { getCouponHint, getMarketingAttribution } from "../utils/affiliateStorage";
import { trackGa4 } from "../utils/analytics";

/**
 * Sign up page component that assembles all auth-related components
 * Uses glassmorphism styling for a modern, translucent appearance
 *
 * @returns {JSX.Element} - Rendered sign up page
 */
const SignUp: React.FC = () => {
  const { signUp, loading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const stateReturn = safeInternalPath((location.state as any)?.from);
  const returnTo = stateReturn || peekAuthReturn();

  useEffect(() => {
    if (stateReturn) rememberAuthReturn(stateReturn);
  }, [stateReturn]);

  /**
   * Handle sign up form submission
   * @param {string} name - User's full name
   * @param {string} email - User email
   * @param {string} password - User password
   */
  const handleSignUp = async (
    name: string,
    email: string,
    password: string
  ) => {
    setSignUpError(null);
    const { error } = await signUp(email, password, {
      data: {
        full_name: name,
        ...buildSignupJourneyMetadata({
          returnTo,
          attribution: getMarketingAttribution() as Record<string, unknown> | null,
          coupon: getCouponHint()?.code,
        }),
      },
      emailRedirectTo: `${window.location.origin}/login?auth_callback=1`,
    });

    if (error) {
      // Set error message for display
      setSignUpError(error.message || "Failed to sign up. Please try again.");
      // Show error notification
      (window as any).showNotification?.(
        "Sign up failed. Please try again.",
        "error"
      );
    } else {
      trackGa4("sign_up", { method: "email" });
      // Signup successful - now handle post-signup actions (coupon redemption)
      // Note: We need to wait a moment for the user to be available in the auth context
      setTimeout(async () => {
        try {
          // The user ID should be available in the supabase auth response
          // For now, we'll use a different approach - check if there's a stored coupon and handle it on login

          // Show basic success notification first
          (window as any).showNotification?.(
            `🎉 Account created successfully! Check your inbox at ${email} to confirm your email address.`,
            "success"
          );

          // Redirect to login page
          navigate("/login", {
            state: {
              message:
                "Your account has been created! Please verify your email to continue.",
              shouldProcessWelcomeBonus: true, // Flag to process coupon on login
              from: returnTo || undefined,
            },
          });
        } catch (postSignupError) {
          console.error("Post-signup processing error:", postSignupError);
          // Still show success and redirect
          (window as any).showNotification?.(
            `🎉 Account created successfully! Check your inbox at ${email} to confirm your email address.`,
            "success"
          );
          navigate("/login", {
            state: {
              message:
                "Your account has been created! Please verify your email to continue.",
              from: returnTo || undefined,
            },
          });
        }
      }, 1000);
    }
  };

  return (
    <>
      <div className={styles.authLayoutOuter}>
        <div className={styles.authLayoutContent}>
          {/* Left panel with welcome message */}
          <div className={styles.leftPanel}>
            <WelcomePanel
              title="Start with C-Shaped."
              subtitle="Create your account and begin studying in a focused clinical workspace."
            />
          </div>

          {/* Right panel with sign up form */}
          <div className={styles.rightPanel}>
            <SignUpForm
              onSubmit={handleSignUp}
              isLoading={loading}
              error={signUpError}
              returnTo={returnTo}
            />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignUp;
